import React from 'react';
import { PhoneNumber } from '@weave/schema-gen-ts/dist/phonenumbers/phonenumber.pb';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { TaskSource, TaskStatus, TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { Task as TaskRecord } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/task.pb';
import {
  ListTasksRequest,
  ListTasksResponse,
  Filter as TasksFilter,
} from '@weave/schema-gen-ts/dist/schemas/task-center/v1/task_service.pb';
import {
  AnalysisType as AnalysisTypeEnum,
  AppointmentType as AppointmentTypeEnum,
  CallDirection as CallDirectionEnum,
  CallStatus as CallIntelCallStatus,
  Category as CategoryEnum,
  ContactType as ContactTypeEnum,
  SchedulingOutcome as SchedulingOutcomeV2Enum,
  Sentiment as SentimentEnum,
  ServiceQualityFlag as ServiceQualityFlagEnum,
  TimePeriodType as TimePeriodTypeEnum,
  Rating,
  IncludesAnalysis,
  IncludesHydration,
  FilterType_index as FilterTypeEnum,
  CallVisibility as CallVisibilityEnum,
  SortType,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { Call as CallRecord } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { SchemaIO } from '@frontend/schema';
import { SchemaCallIntelV2Service, SchemaTaskService } from '../../api-call-intel/src/service';
import {
  AllowedServiceIssuesRouteLabelMap,
  INFINITE_QUERY_ENDPOINT_NAMES,
  MUTATION_ENDPOINT_NAMES,
  QUERY_ENDPOINT_NAMES,
  STANDARD_QUERY_ENDPOINT_NAMES,
  STANDARD_QUERY_TASK_SERVICE_ENDPOINT_NAMES,
  TASK_QUERY_ENDPOINT_NAMES,
} from './constant';

export type StandardQueryEndpointName = (typeof STANDARD_QUERY_ENDPOINT_NAMES)[number];
export type StandardTaskQueryEndpointName = (typeof STANDARD_QUERY_TASK_SERVICE_ENDPOINT_NAMES)[number];
export type InfiniteQueryEndpointName = (typeof INFINITE_QUERY_ENDPOINT_NAMES)[number];
export type QueryEndpointName = (typeof QUERY_ENDPOINT_NAMES)[number];
export type TaskQueryEndpointName = (typeof TASK_QUERY_ENDPOINT_NAMES)[number];

export type MutationEndpointName = (typeof MUTATION_ENDPOINT_NAMES)[number];

export type ServiceStandardQueries = Pick<typeof SchemaCallIntelV2Service, StandardQueryEndpointName>;
export type ServiceTaskStandardQueries = Pick<typeof SchemaTaskService, StandardTaskQueryEndpointName>;
export type ServiceInfiniteQueries = Pick<typeof SchemaCallIntelV2Service, InfiniteQueryEndpointName>;
export type ServiceQueries = Pick<typeof SchemaCallIntelV2Service, QueryEndpointName>;
export type TaskServiceQueries = Pick<typeof SchemaTaskService, TaskQueryEndpointName>;
export type ServiceMutations = Pick<typeof SchemaCallIntelV2Service, MutationEndpointName>;

/**
 *  “IO type” per endpoint for convenience:
 */

export type GetCallsOverviewIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetCallsOverview']>;
export type GetOverviewStatsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['OverviewStats']>;
export type GetCallEditHistoryIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetCallEditHistory']>;
export type GetCallMetadataIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetCallMetadata']>;
export type GetServiceQualityStatsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetServiceQualityStats']>;

export type DemoServiceQualityIO = SchemaIO<(typeof SchemaCallIntelV2Service)['DemoServiceQuality']>;

export type SubmitFeedbackIO = SchemaIO<(typeof SchemaCallIntelV2Service)['SubmitFeedback']>;
export type EditAIOutputIO = SchemaIO<(typeof SchemaCallIntelV2Service)['EditAIOutput']>;
export type RecentCallsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['RecentCalls']>;
export type ListCallsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['ListCalls']>;
export type CreateSuggestionIO = SchemaIO<(typeof SchemaCallIntelV2Service)['CreateSuggestion']>;
export type ListFollowupsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['ListFollowups']>;
export type GetCallFollowupsStatsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetCallFollowupsStats']>;
export type GetSchedulingAggregateStatsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetSchedulingAggregateStats']>;

export type ListTaskIO = SchemaIO<(typeof SchemaTaskService)['ListTasks']>;
export type UpdateTaskIO = SchemaIO<(typeof SchemaTaskService)['UpdateTask']>;
export type DeleteTaskIO = SchemaIO<(typeof SchemaTaskService)['DeleteTask']>;

export enum CallStatus {
  CALL_STATUS_COMPLETE = CallIntelCallStatus.CALL_STATUS_COMPLETE,
  CALL_STATUS_ERROR = CallIntelCallStatus.CALL_STATUS_ERROR,
  CALL_STATUS_FAILED = CallIntelCallStatus.CALL_STATUS_FAILED,
  CALL_STATUS_INFERENCING = CallIntelCallStatus.CALL_STATUS_INFERENCING,
  CALL_STATUS_PENDING = CallIntelCallStatus.CALL_STATUS_PENDING,
  CALL_STATUS_SKIPPED = CallIntelCallStatus.CALL_STATUS_SKIPPED,
  CALL_STATUS_TRANSCRIBING = CallIntelCallStatus.CALL_STATUS_TRANSCRIBING,
}

export enum AppointmentTypeDentalEnum {
  APPOINTMENT_TYPE_UNKNOWN = 'APPOINTMENT_TYPE_UNKNOWN',
  APPOINTMENT_TYPE_IMAGING = 'APPOINTMENT_TYPE_IMAGING',
  APPOINTMENT_TYPE_COSMETIC = 'APPOINTMENT_TYPE_COSMETIC',
  APPOINTMENT_TYPE_ORTHODONTICS = 'APPOINTMENT_TYPE_ORTHODONTICS',
  APPOINTMENT_TYPE_SURGERY = 'APPOINTMENT_TYPE_SURGERY',
  APPOINTMENT_TYPE_PREVENTIVE = 'APPOINTMENT_TYPE_PREVENTIVE',
  APPOINTMENT_TYPE_HYGIENE = 'APPOINTMENT_TYPE_HYGIENE',
  APPOINTMENT_TYPE_RESTORATIVE = 'APPOINTMENT_TYPE_RESTORATIVE',
  APPOINTMENT_TYPE_OTHER = 'APPOINTMENT_TYPE_OTHER',
}

export enum AppointmentTypeOptoEnum {
  APPOINTMENT_TYPE_UNKNOWN = 'APPOINTMENT_TYPE_UNKNOWN',
  APPOINTMENT_TYPE_EYE_EXAM = 'APPOINTMENT_TYPE_EYE_EXAM',
  APPOINTMENT_TYPE_SURGERY = 'APPOINTMENT_TYPE_SURGERY',
  APPOINTMENT_TYPE_VISION_CORRECTION = 'APPOINTMENT_TYPE_VISION_CORRECTION',
  APPOINTMENT_TYPE_MEDICAL_EYE_CARE = 'APPOINTMENT_TYPE_MEDICAL_EYE_CARE',
  APPOINTMENT_TYPE_SPECIALTY_SERVICES = 'APPOINTMENT_TYPE_SPECIALTY_SERVICES',
  APPOINTMENT_TYPE_OTHER = 'APPOINTMENT_TYPE_OTHER',
}

export enum AppointmentTypeVetEnum {
  APPOINTMENT_TYPE_UNKNOWN = 'APPOINTMENT_TYPE_UNKNOWN',
  APPOINTMENT_TYPE_SICK_PET = 'APPOINTMENT_TYPE_SICK_PET',
  APPOINTMENT_TYPE_PREVENTIVE_CARE = 'APPOINTMENT_TYPE_PREVENTIVE_CARE',
  APPOINTMENT_TYPE_DENTAL = 'APPOINTMENT_TYPE_DENTAL',
  APPOINTMENT_TYPE_SURGERY = 'APPOINTMENT_TYPE_SURGERY',
  APPOINTMENT_TYPE_DIAGNOSTICS = 'APPOINTMENT_TYPE_DIAGNOSTICS',
  APPOINTMENT_TYPE_END_OF_LIFE_CARE = 'APPOINTMENT_TYPE_END_OF_LIFE_CARE',
  APPOINTMENT_TYPE_OTHER = 'APPOINTMENT_TYPE_OTHER',
}

export enum FollowupTaskFilterEnum {
  FILTER_BY_DATE_RANGE = 1,
  FILTER_BY_LOCATION = 2,
  FILTER_BY_OFFICE_USER = 3,
  FILTER_BY_CONTACT_TYPE = 4,
  FILTER_BY_TASK_TYPE = 5,
  FILTER_BY_TASK_STATUS = 6,
}

export enum FollowupSortFieldEnum {
  SORT_UNKNOWN = 0,
  SORT_LAST_CALL = 1,
  SORT_CALLS_COUNT = 2,
}

export enum SchedulingOpportunityEnum {
  SCHEDULING_OPPORTUNITY_IDENTIFIED = 'SCHEDULING_OPPORTUNITY_IDENTIFIED',
  SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED = 'SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED',
}

export enum SchedulingOutcomeEnum {
  SCHEDULING_OUTCOME_UNKNOWN = 'SCHEDULING_OUTCOME_UNKNOWN',
  SCHEDULING_OUTCOME_SCHEDULED = 'SCHEDULING_OUTCOME_SCHEDULED',
  SCHEDULING_OUTCOME_UNSCHEDULED = 'SCHEDULING_OUTCOME_UNSCHEDULED',
}

export enum SearchTypeEnum {
  SEARCH_UNKNOWN = 0,
  SEARCH_BY_NAME = 1,
  SEARCH_BY_NUMBER = 2,
}

/**
 * @deprecated - use SortType which is re-exported from call-intelligence/enums.pb instead. This will be removed in future versions.
 */
export enum SortTypeEnum {
  SORT_ASC = 1,
  SORT_DESC = 2,
}

export type AggregateChart = {
  aggregates: HourlyAggregatesByDay[];
};

export type ApiPayloadProps = {
  drillDownOptions?: DrillDownOptions;
  filters: Filters;
  includesAnalysis?: IncludesAnalysis[];
  includesHydration?: IncludesHydration[];
};

export type ApiSearchPayloadProps = {
  search_type: SearchTypeEnum;
  search: string;
};

export type ApiOverviewStatsParams = {
  filters: Filters;
  includes: StatsIncludes;
};

export type CallSentiment = {
  sentiment: SentimentEnum;
};

/**
 * @deprecated Call type is deprecated, use CallRecord instead. Call type will be removed in future versions.
 */
export type Call = Omit<
  CallRecord,
  | 'callSchedulingOpportunity'
  | 'callSchedulingOutcome'
  | 'callSentiment'
  | 'id'
  | 'locationId'
  | 'direction'
  | 'phoneNumber'
  | 'officeUser'
> & {
  callSchedulingOpportunity?: boolean | null;
  callSchedulingOutcome?: boolean | null;
  callSentiment?: CallSentiment | null;
  id: string;
  locationId: string;
  direction: CallDirectionEnum;
  phoneNumber: PhoneNumber;
  officeUser: OfficeUser | null;
};

export type CallIntelTab = 'overview' | 'follow-up' | 'records';

export type CallMetadata = Omit<CallMetadataResponse, 'callMetadata'> & {
  callParticipantsMetadata: CallParticipantsMetadata;
  tasks: Task[];
};

export type CallMetadataResponse = {
  rating?: keyof typeof Rating;
  summary: string;
  transcript: string;
  callMetadata: CallParticipantsMetadata;
};

export type CallsStats = {
  count?: number;
};

export type CallParticipantsMetadata = { caller: string; receptionist: string };

export type CallsResponse = {
  calls?: Call[];
  newCalls?: Call[];
  hasNextPage?: boolean;
  pageConfig: PageConfig;
};

export type CallTaskListResponse = ListTasksResponse;
export interface CallTaskListRequest extends Omit<ListTasksRequest, 'filter'> {
  filter?: Record<number, Partial<TasksFilter>>;
}

export type CallTaskParams = {
  locations?: string[];
  source?: TaskSource;
  callIds?: string[];
  taskStatusFilters?: TaskStatus[];
  taskTypes?: TaskType[];
  demoMetadata?: CallTaskListRequest['demoMetadata'] & {
    callRecordList?: HydratedCallRecord[];
  };
} & Omit<CallTaskListRequest, 'demoMetadata'>;

export type CallInsightAccordion = 'call-tasks' | 'call-summary' | 'call-analysis' | 'call-details' | 'service-quality';

export type ContactTypeFilterEnum = Omit<ContactTypeEnum, ContactTypeEnum.CONTACT_UNKNOWN>;

export type CommonFilterPayloadParams = {
  filters: Filters;
  dateRangeParams?: {
    timePeriod?: TimePeriodTypeEnum;
    timezone?: string;
  };
};

export type DrillDownOptions = {
  index: FilterTypeEnum;
  key: string;
  value: string[];
};

export type DateInterval = {
  startDate: string;
  endDate: string;
};

export type FilterPayload = {
  filter: Partial<Record<FilterTypeEnum, Record<string, unknown>>>;
  limit?: number;
  offset?: number;
  sort_type?: SortTypeEnum;
};

export type Filters = {
  aiOutcomes?: string[];
  appointmentTypes?: string[];
  callDirection?: CallDirectionEnum;
  contactTypes?: ContactTypeFilterEnum[];
  categories?: string[];
  endDate?: string;
  hideNonAnalyzedCalls?: boolean;
  locations?: string[];
  officeNumbers?: string[];
  officeUsers?: string[];
  schedulingOpportunity?: string;
  schedulingOutcomes?: SchedulingOutcomeEnum[];
  schedulingOutcomeV2s?: SchedulingOutcomeV2Enum[];
  sentiments?: string[];
  serviceQualityFlags?: string[];
  startDate?: string;
  taskTypes?: TaskType[];
};

export type FollowUp = {
  calls: CallRecord[];
  newCalls: CallRecord[];
  totalTasks: number;
  totalCalls: number;
};

export type FollowUpItem = {
  allCalls: CallRecord[];
  contact: CallRecord & { contactId: string };
  callTaskMapping: Record<string, Task[]>;
  totalCalls: number;
  totalTasks: number;
};

export type FollowUpResponse = {
  followUps: FollowUp[];
  hasPrivateCalls?: boolean;
  hasNextPage?: boolean;
  pageConfig: PageConfig;
  totalFollowUps: number;
};

export type FollowUpStatsResponse = {
  stats: TaskStats;
};

export type FollowupTaskTypeFilters = {
  startDate?: string;
  endDate?: string;
  locations?: string[];
  officeUsers?: string[];
  contactTypes?: ContactTypeFilterEnum[];
  taskTypes?: TaskType[];
  taskStatus?: TaskStatus[];
};

export type GetFollowUpsParams = {
  filters: FollowupTaskTypeFilters;
  sortBy: FollowupSortFieldEnum;
  sortType?: SortTypeEnum;
  searchQuery?: string;
  pageConfig: PageConfig;
};

export type GetFollowUpsStatsParams = {
  filters: FollowupTaskTypeFilters;
};

export type HighlightedCitation = {
  text: string;
  time: string;
};

export type HourlyAggregatesByDay = {
  date: string; // Format: 'yyyy-mm-dd'
  values: string[]; // Array of values, representing 24 hourly values
};

export type InfoTipId =
  | 'aiCallOutcome'
  | 'callsAnalyzed'
  | 'callsByAppointmentType'
  | 'callsByCategory'
  | 'callsToReview'
  | 'callEscalationServiceIssue'
  | 'contactType'
  | 'conversionRate'
  | 'excellentService'
  | 'followUpReason'
  | 'followUpSummary'
  | 'ineffectiveTreatmentServiceIssue'
  | 'repeatCallerServiceIssue'
  | 'schedulingOpportunities'
  | 'schedulingOutcome'
  | 'schedulingOutcomeV2'
  | 'sentiments'
  | 'serviceIssues'
  | 'serviceQuality'
  | 'serviceQualityCallVolume'
  | 'serviceQualityPeriodOverPeriod'
  | 'spotLightedCalls'
  | 'tasks'
  | 'taskTypeBilling'
  | 'taskTypeInsurance'
  | 'taskTypeOther'
  | 'taskTypePatientCare'
  | 'taskTypeScheduling'
  | 'taskTypeWaitlist'
  | 'unresolved'
  | 'unscheduledOpportunitiesByLocation'
  | 'activeOpportunities'
  | 'conversionDuringCall';

export type InsightChartType = 'bar' | 'gauge';
export type InsightType = 'scheduled' | 'unresolved';

export type IsFieldEdited = {
  isAppointmentTypeEdited: boolean;
  isCategoryEdited: boolean;
  isContactTypeEdited: boolean;
  isSchedulingOpportunityEdited: boolean;
  isSchedulingOutcomeEdited: boolean;
  isSentimentEdited: boolean;
  isServiceQualityFlagEdited: boolean;
};

export type Trend = {
  change: ChangeType;
  percentageChange?: number;
  tooltip?: React.ReactNode;
  partialNote?: string;
  hideWhenNoPrev?: boolean;
  polarity?: Polarity; // Coloring semantics (default 'upGood'); use 'downGood' when lower is better
};

export type KpiItem = {
  id: string;
  title?: React.ReactNode;
  value?: number | null;
  format?: 'integer' | 'percent' | ((n: number | null) => string);
  subtext?: React.ReactNode;
  trend?: Trend;
  noCallsToAnalyze?: boolean;
};

export type ListCallBaseRequestParams = {
  drillDownOptions?: DrillDownOptions;
  filters: Filters;
  showOnlyUnscheduled?: boolean;
  searchQuery?: string;
  sortType?: SortType;
  includesAnalysis?: IncludesAnalysis[];
  callVisibility?: CallVisibilityEnum;
};

export type ListCallsRequestPayload = ListCallBaseRequestParams & {
  limit: number;
};

type Location = {
  id: string;
  name: string;
  slug: string;
};

export type Locations = {
  locations: Location[];
};

export type OpportunitiesByLocation = {
  totalOpportunities: number;
  unscheduledOpportunities: number;
};

export type Overview = {
  appointmentTypes: Partial<Record<AppointmentTypeEnum, number>> | null;
  categories: Partial<Record<CategoryEnum, number>> | null;
  sentiments: Partial<Record<SentimentEnum, number>> | null;
  summary: Summary;
  serviceQualitySummary: ServiceQualitySummary;
  taskTypes: Partial<Record<TaskType, number>> | null;
  totalCallsAnalyzed: number;
};

export type PageConfig = {
  pageNumber: number;
  pageSize: number;
};

export type PatientProvidedInfo = {
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  ssn?: string;
  dob?: string;
  faxNumber?: string;
  dependentName?: string[];
  dependentPhone?: string[];
  dependentEmail?: string[];
  dependentDob?: string[];
  dependentSsn?: string[];
  insuranceProvider?: string;
  insurancePhoneNumber?: string;
  insuranceFax?: string;
  insuranceEmail?: string;
  insuranceMemberId?: string[];
};

export type SearchState = { query: string; value: string };

export type SelectedTimePeriodKey = string | null;

export type ServiceQualityFlag = Exclude<ServiceQualityFlagEnum, ServiceQualityFlagEnum.FLAG_UNKNOWN>;

export type ServiceQualityFlagData = {
  summary: string;
  tsStart: string;
};

export type ServiceQualityFlagTimeMapping = {
  [time: string]: { type: ServiceQualityFlagEnum };
};

export type ServiceQualityMetadata = {
  flagData: ServiceQualityResponse;
  flagTimeMapping: ServiceQualityFlagTimeMapping;
};

export type ServiceQualityCallVolumeResponse = {
  callsAnalyzed: AggregateChart;
  excellentService: AggregateChart;
  unresolved: AggregateChart;
};

export type ServiceQualityResponse = {
  isIssuesDisabled: boolean;
  isResolutionDisabled: boolean;
  issues: ServiceQualityFlagData[];
  resolution: ServiceQualityFlagData[];
};

export type StatsIncludes = {
  opportunities?: boolean;
  serviceQuality?: boolean;
};

type Summary = {
  isPartial: boolean;
  scheduledOpportunities?: number;
  totalOpportunities?: number;
};

export type ServiceQualitySummary = {
  callsToReview: number;
  callsResolved: number;
  isPartial: boolean;
};

export type ServiceQualityStats = GetServiceQualityStatsIO['output'];

export type OverviewStatsResponse = {
  serviceQualitySummary: ServiceQualitySummary;
  summary: Summary;
  totalCallsAnalyzed: number;
};

export type DemoServiceQualityChartStatsResponse = {
  stats: OverviewStatsResponse;
  callVolume: ServiceQualityCallVolumeResponse;
};

export type Task = {
  citation?: string;
  citationTime?: string;
} & TaskRecord;

export type TaskStats = {
  [key in TaskStatus]?: number;
};

export type TaskTypeInfereces = {
  inferences: Partial<Record<TaskType, number>>;
};

export type TaskUpdateRequest = {
  id: string;
  status: TaskStatus;
};

export type TranscriptTimeDataMapping = {
  [time: string]: { popoverLabel: string; hightlightColor: string };
};

export type OfficeUser = {
  firstName?: string;
  lastName?: string;
  status?: string;
  userId?: string;
  userName?: string;
};

export type UnsavedEditChangesConfig = {
  actionType?: 'CLOSE' | 'DISCARD' | 'NO_ACTION' | 'SAVE' | 'SHOW_MODAL';
  dontAskAgain?: boolean;
  entity?: string;
  hasUnsavedChanges: boolean;
};

/**These needs to be organised as per alphabetical order */

export type GetCallEditHistoryResponse = {
  original: CallEditableFields;
  current: CallEditedField[];
};

export type ServiceQualityFlagState = {
  serviceQualityFlag: ServiceQualityFlag;
  isDisabled: boolean;
};

export type CallEditableFields = {
  appointmentTypes?: null | { appointmentType: AppointmentTypeEnum[] };
  callSentiment?: CallSentiment | null;
  categories?: CategoryEnum[];
  contactType?: ContactTypeEnum;
  schedulingOpportunity?: boolean | null;
  schedulingOutcome?: boolean | null;
  schedulingOutcomeV2?: SchedulingOutcomeV2Enum;
  serviceQualityFlags?: ServiceQualityFlagState[];
};

export type CallEditedField = {
  fields: CallEditableFields;
  analysisType: AnalysisTypeEnum;
  editedBy: OfficeUser;
  editedAt: string;
};

export type CreateSuggestionRequest = {
  callId: string;
  location_id: string;
  user_id: string;
  suggestion: string;
  vertical?: string;
  type: AnalysisTypeEnum;
};

export type TransformEditableFieldType = 'edit' | 'restore';

export type ServiceIssueSlug = keyof typeof AllowedServiceIssuesRouteLabelMap;

export type ChangeType = 'decreased' | 'increased' | 'noData' | 'unchanged';
export type Polarity = 'upGood' | 'downGood' | 'neutral';

export type ExtendedUpdateTaskRequest = UpdateTaskIO['input'] & {
  callId?: string;
  oldStatus?: TaskStatus;
  taskType?: TaskType;
};

export type ListCallsRequest = SchemaIO<(typeof SchemaCallIntelV2Service)['ListCalls']>['input'];
export type ListCallsResponse = SchemaIO<(typeof SchemaCallIntelV2Service)['ListCalls']>['output'];

export type CallIntelSubView = {
  id?: CallIntelSubViewId;
  type?: CallIntelSubViewType;
};

export type CallIntelSubViewType = 'appointment-type' | 'category' | 'sentiment' | 'service-quality';
export type CallIntelSubViewId = string | AppointmentTypeEnum | CategoryEnum | SentimentEnum;
export type PanelSource =
  | 'callIntel'
  | 'callHistoryContactPanel'
  | 'patientInsightsContactPanel'
  | 'dashboardUnhappyWidget'
  | 'taskCenter';

export {
  AnalysisType as AnalysisTypeEnum,
  AppointmentType as AppointmentTypeEnum,
  CallDirection as CallDirectionEnum,
  Category as CategoryEnum,
  ContactType as ContactTypeEnum,
  IncludesAnalysis,
  IncludesHydration,
  SchedulingOutcome as SchedulingOutcomeV2Enum,
  Sentiment as SentimentEnum,
  ServiceQualityFlag as ServiceQualityFlagEnum,
  TimePeriodType as TimePeriodTypeEnum,
  WidgetType as WidgetTypeEnum,
  TimePeriod as DemoTimePeriodEnum,
  RequestSource as CallMetadataRequestSource,
  FilterType_index as FilterTypeEnum,
  FollowupsSortBy,
  SortType,
  Feature as FeedbackFeatureEnum,
  SubIssue as FeedbackSubIssueEnum,
  CallVisibility as CallVisibilityEnum,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';

export type { Call as CallRecord } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';

export type {
  DateRange,
  Filter as ListCallFilter,
  Person,
  GetCallsViewByLocationResponse as CallsViewByLocation,
} from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2/call_intelligence.pb';

export type { PhoneNumber } from '@weave/schema-gen-ts/dist/phonenumbers/phonenumber.pb';
export {
  FilterType_index as TaskFilterTypeEnum,
  SortType as TaskSortTypeEnum,
  TaskSource as TaskSourceEnum,
  TaskStatus,
  TaskType,
} from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';

export type CallIntelDemoTriggerSource =
  | 'DASHBOARD_WIDGETS_DEMO'
  | 'FULL_MODULE_DEMO'
  | 'RECENT_CALLS_PAGE'
  | 'CALL_INSIGHT_PANEL';

export type GetOverviewStatsRequstParams = ApiOverviewStatsParams &
  ApiPayloadProps &
  Pick<GetOverviewStatsIO['input'], 'demoMetadata' | 'isDemo'>;

export type GetOverviewDetailsRequstParams = ApiPayloadProps &
  Pick<GetCallsOverviewIO['input'], 'demoMetadata' | 'isDemo'>;

export type CallIntelSortOption =
  | 'callCountHL'
  | 'callCountLH'
  | 'contactNameAZ'
  | 'contactNameZA'
  | 'lastCallLatest'
  | 'lastCallOldest'
  | 'taskCountHL'
  | 'taskCountLH';
